import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Paragraph,
  Switch,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { useSaveHmsSettingsMutation } from '../../../../hooks/queries/hms/useHmsSettingsQuery';
import {
  confirmsTurningOff,
  safetyRepsChangedText,
  safetyRepsRequiredText,
  warnsWithoutSafetyReps,
} from './hrOverviewView';
import classes from './Overview.module.css';

/**
 * Norway requires safety representatives. An organisation elsewhere turns them off, and then
 * the team leader assesses and approves alone, with nothing about safety representatives shown.
 */
export const SafetyRepSetting = ({
  setup,
}: {
  setup: HrWorkEnvironmentOverview['setup'];
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const save = useSaveHmsSettingsMutation();
  const [confirming, setConfirming] = useState(false);
  const { usesSafetyReps } = setup;
  const changed = safetyRepsChangedText(setup, t, i18n.language);
  const requiredText = safetyRepsRequiredText(setup, t);

  const choose = (chosen: boolean) => {
    if (confirmsTurningOff(setup, chosen)) {
      setConfirming(true);
      return;
    }
    save.mutate({ usesSafetyReps: chosen });
  };

  const turnOff = () =>
    save.mutate(
      { usesSafetyReps: false },
      { onSuccess: () => setConfirming(false) },
    );

  return (
    <div className={classes.setting}>
      <Switch
        data-size="sm"
        label={t('hrOverview.setup.usesSafetyReps')}
        description={t(
          usesSafetyReps
            ? 'hrOverview.setup.usesSafetyRepsOn'
            : 'hrOverview.setup.usesSafetyRepsOff',
        )}
        checked={save.isPending ? !usesSafetyReps : usesSafetyReps}
        onChange={(event) => choose(event.target.checked)}
      />
      {confirming && (
        <Alert data-color="warning" data-size="sm">
          <Paragraph data-size="sm">{requiredText}</Paragraph>
          <Paragraph data-size="sm">
            {t('hrOverview.setup.turnOffQuestion')}
          </Paragraph>
          <div className={classes.links}>
            <Button
              variant="secondary"
              data-size="sm"
              loading={save.isPending}
              onClick={turnOff}
            >
              {t('hrOverview.setup.turnOff')}
            </Button>
            <Button
              variant="tertiary"
              data-size="sm"
              onClick={() => setConfirming(false)}
            >
              {t('hrOverview.setup.cancel')}
            </Button>
          </div>
        </Alert>
      )}
      {warnsWithoutSafetyReps(setup) && (
        <Alert data-color="warning" data-size="sm">
          {requiredText}
        </Alert>
      )}
      {changed && (
        <Paragraph data-size="xs" className={classes.subtle}>
          {changed}
        </Paragraph>
      )}
      {save.isSuccess && (
        <Paragraph data-size="xs" role="status" className={classes.subtle}>
          {t('hrOverview.setup.saved')}
        </Paragraph>
      )}
      {save.isError && (
        <ValidationMessage data-size="sm">
          {t('hrOverview.setup.saveFailed')}
        </ValidationMessage>
      )}
    </div>
  );
};
